import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, Trash2, MoreVertical, Save, X } from "lucide-react";
import {
  listLookupValues,
  createLookupValue,
  bulkCreateLookupValues,
  updateLookupValue,
  deleteLookupValue,
} from "../../../../../Services/adminCorporates";
import ConfirmDialog from "../../../../../Components/admin/shared/ConfirmDialog";
import EmptyState from "../../../../../Components/admin/shared/EmptyState";
import { Button } from "../../../../../ui/button";
import { Input } from "../../../../../ui/input";
import { Switch } from "../../../../../ui/switch";
import { Card, CardContent } from "../../../../../ui/card";
import { Skeleton } from "../../../../../ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../../../../ui/dropdown-menu";

/**
 * Shared editor for the two lookup-value lists (departments / roles). The
 * `kind` prop drives all the labels and API calls.
 *
 * Responsibilities:
 *  - List active + inactive values (with active toggle)
 *  - Add a single value via inline form
 *  - Bulk-import via textarea (one per line)
 *  - Inline rename
 *  - Delete with confirm dialog warning that pending members may be affected
 */
const LookupValuesEditor = ({ kind, singular, plural }) => {
  const { id } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add-single state
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  // Bulk import state
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Inline edit state — keyed by value id
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listLookupValues(id, kind);
      // Sort by order, then name as a fallback
      const sorted = [...(data?.items || [])].sort((a, b) => {
        if (a.order !== b.order) return (a.order ?? 0) - (b.order ?? 0);
        return (a.name || "").localeCompare(b.name || "");
      });
      setItems(sorted);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [id, kind]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setAdding(true);
    setAddError(null);
    try {
      await createLookupValue(id, kind, {
        name: trimmed,
        order: items.length,
        is_active: true,
      });
      setNewName("");
      load();
    } catch (err) {
      setAddError(
        err?.response?.data?.message || err?.message || `Failed to add ${singular}`
      );
    } finally {
      setAdding(false);
    }
  };

  const handleBulkSubmit = async () => {
    const names = bulkText
      .split(/\n+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (names.length === 0) return;
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const result = await bulkCreateLookupValues(id, kind, names);
      setBulkResult(result);
      setBulkText("");
      load();
    } catch (err) {
      setBulkResult({
        error: err?.response?.data?.message || err?.message || "Bulk import failed",
      });
    } finally {
      setBulkBusy(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditName(item.name);
  };

  const saveEdit = async (item) => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === item.name) {
      setEditingId(null);
      return;
    }
    try {
      await updateLookupValue(id, kind, item.id, { name: trimmed });
      setEditingId(null);
      load();
    } catch (err) {
      // Surface inline; let user retry without losing focus.
      setError(err?.response?.data?.message || err?.message || "Failed to update");
    }
  };

  const toggleActive = async (item) => {
    try {
      await updateLookupValue(id, kind, item.id, { is_active: !item.is_active });
      load();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to update");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteLookupValue(id, kind, deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to delete");
    }
  };

  return (
    <div className="space-y-6">
      {/* Add single + bulk import controls */}
      <div className="flex flex-col md:flex-row gap-3 md:items-end">
        <div className="flex-1 grid gap-1.5">
          <label className="text-sm font-medium">Add {singular}</label>
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={`e.g. ICU`}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAdd();
                }
              }}
            />
            <Button onClick={handleAdd} disabled={adding || !newName.trim()}>
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>
          {addError && <p className="text-xs text-destructive">{addError}</p>}
        </div>
        <Button variant="outline" onClick={() => setShowBulk((v) => !v)}>
          {showBulk ? "Hide bulk import" : "Bulk import"}
        </Button>
      </div>

      {showBulk && (
        <Card>
          <CardContent className="pt-6 space-y-3">
            <p className="text-sm text-muted-foreground">
              Paste one {singular} per line. Up to 500 entries; duplicates are
              skipped automatically.
            </p>
            <textarea
              rows={6}
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder={`ICU\nEmergency\nRadiology`}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowBulk(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleBulkSubmit}
                disabled={bulkBusy || !bulkText.trim()}
              >
                {bulkBusy ? "Importing…" : "Import"}
              </Button>
            </div>
            {bulkResult && (
              <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                {bulkResult.error ? (
                  <span className="text-destructive">
                    {typeof bulkResult.error === "string"
                      ? bulkResult.error
                      : bulkResult.error?.message ||
                        "Bulk import failed"}
                  </span>
                ) : (
                  (() => {
                    const createdCount = Array.isArray(bulkResult.created)
                      ? bulkResult.created.length
                      : typeof bulkResult.created === "number"
                      ? bulkResult.created
                      : 0;
                    const skippedCount = Array.isArray(
                      bulkResult.skipped_duplicates
                    )
                      ? bulkResult.skipped_duplicates.length
                      : typeof bulkResult.skipped_duplicates === "number"
                      ? bulkResult.skipped_duplicates
                      : 0;
                    return (
                      <span>
                        Created: {createdCount}
                        {skippedCount > 0 && (
                          <>
                            {" · "}
                            Skipped duplicates: {skippedCount}
                          </>
                        )}
                      </span>
                    );
                  })()
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title={`No ${plural} yet`}
          description={`Add ${plural} that members can pick from on the activation form.`}
        />
      ) : (
        <Card>
          <CardContent className="pt-4 divide-y">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
              >
                <span className="w-8 text-xs text-muted-foreground tabular-nums">
                  {item.order ?? 0}
                </span>
                <div className="flex-1 min-w-0">
                  {editingId === item.id ? (
                    <div className="flex gap-2 items-center">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            saveEdit(item);
                          } else if (e.key === "Escape") {
                            setEditingId(null);
                          }
                        }}
                        autoFocus
                      />
                      <Button size="icon" variant="ghost" onClick={() => saveEdit(item)}>
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setEditingId(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <span
                      className={`text-sm ${
                        item.is_active ? "" : "text-muted-foreground line-through"
                      }`}
                    >
                      {item.name}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={item.is_active !== false}
                    onCheckedChange={() => toggleActive(item)}
                    aria-label="Active"
                  />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => startEdit(item)}>
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setDeleteTarget(item)}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={`Delete ${singular}?`}
        description={
          deleteTarget
            ? `"${deleteTarget.name}" will be removed. Pending members who selected this value will need to pick a different one.`
            : ""
        }
        confirmLabel="Delete"
        destructive
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default LookupValuesEditor;
